import { parseLocal } from "./time";
import { displayMs, type FieldId, formatClock, gameElapsedMs, gameLengthMs, type GameState, type ScheduledGame } from "./types";

/**
 * The board's Live view, as arithmetic (2026-09-19, the owner's pick «Ефір +
 * поля»): the pitches are the control - a pill each, carrying that pitch's
 * score - and everything under them belongs to the chosen pitch: the game on
 * it, what is next there, what was played there today. Pure, so the view and
 * its tests read one rule for "which pitch opens" and "what is next here".
 */

/** What a pitch is doing: a game between its first pull and the final, a
 *  final still on the scorekeeper's phone, or nothing worth a score. */
export type PitchPhase = "live" | "final" | "idle";

/** No game worth a score: an untouched placeholder matchup at 0-0. */
export function isIdle(state: GameState): boolean {
  return state.period === "pregame" && state.teamA.score === 0 && state.teamB.score === 0;
}

export function pitchPhase(state: GameState | null): PitchPhase {
  if (state === null || isIdle(state)) return "idle";
  return state.period === "final" ? "final" : "live";
}

/** The public live clock keeps showing time played beyond the countdown's
 * limit. Use the game's elapsed clock so pauses, corrections and server skew
 * behave exactly as before; the scorekeeper still decides when it is final. */
export function liveGameClock(
  state: Pick<GameState, "clock" | "config" | "priorMs">,
  now: number,
): string {
  const over = gameElapsedMs(state, now) - gameLengthMs(state.config);
  if (state.config.clockMode === "countdown" && over > 0) return `+${formatClock(over)}`;
  return formatClock(displayMs(state, now));
}

export interface PitchLane {
  /** Unplayed games here, soonest first, of the soonest playing day only. */
  later: ScheduledGame[];
  /** Today's results here, newest first. */
  earlier: ScheduledGame[];
}

/** A game two hours past its start with no result was skipped or abandoned;
 *  it must not sit on a pitch as "next" all day (the Next view's rule). */
const STALE_MS = 2 * 60 * 60_000;

/**
 * One pitch's day around the game that is on it.
 *
 * `onNow` is the schedule row the pitch's hero already shows (a live game, or
 * a final the scorekeeper has not replaced yet): it is left out of both lists,
 * or a finished game would sit in the hero AND lead «раніше» under it.
 *
 * ONE day of what is next, the Next view's rule: Saturday's last pitch would
 * otherwise list Sunday's 09:00 under a Saturday 17:10 as if an hour apart.
 */
export function pitchLane(
  schedule: readonly ScheduledGame[],
  fieldId: FieldId,
  at: { today: string; now: number; onNow: string | null },
): PitchLane {
  const here = schedule.filter((g) => g.fieldId === fieldId && g.id !== at.onNow);
  const ahead = here
    .filter((g) => {
      if (g.result !== undefined) return false;
      const start = parseLocal(g.startAt);
      return start === null || start.getTime() >= at.now - STALE_MS;
    })
    .sort((x, y) => x.startAt.localeCompare(y.startAt));
  const day = ahead[0]?.startAt.slice(0, 10);
  return {
    later: ahead.filter((g) => g.startAt.slice(0, 10) === day),
    earlier: here
      .filter((g) => g.result !== undefined && g.startAt.slice(0, 10) === at.today)
      .sort((x, y) => y.startAt.localeCompare(x.startAt)),
  };
}

export interface PitchBrief {
  fieldId: FieldId;
  phase: PitchPhase;
  /** One of the viewer's own squads is in the game on this pitch. */
  mine: boolean;
  /** The next kickoff here, as the schedule writes it (zone-less, sortable). */
  nextStartAt: string | undefined;
}

/**
 * Which pitch the view opens on until the viewer picks one: their own squad's
 * game, else the first pitch in play, else a final still showing, else the
 * soonest kickoff, else the first pitch. In the fields' own order throughout,
 * so two pitches in play open on the main one.
 */
export function openingPitch(pitches: readonly PitchBrief[]): FieldId | null {
  const first = (test: (p: PitchBrief) => boolean): FieldId | undefined =>
    pitches.find(test)?.fieldId;
  const soonest = [...pitches]
    .filter((p) => p.nextStartAt !== undefined)
    .sort((x, y) => (x.nextStartAt ?? "").localeCompare(y.nextStartAt ?? ""))[0]?.fieldId;
  return (
    first((p) => p.phase === "live" && p.mine) ??
    first((p) => p.phase === "live") ??
    first((p) => p.phase === "final") ??
    soonest ??
    pitches[0]?.fieldId ??
    null
  );
}

/**
 * `01:19:47` above an hour, `19:47` under it, null once the kickoff has come:
 * the hero then says "starting" in words, because a scorekeeper starts a game
 * by hand and a clock counting up past 0:00 would read as the game's own.
 */
export function kickoffCountdown(startAt: string, now: number): string | null {
  const start = parseLocal(startAt);
  if (start === null) return null;
  const left = Math.floor((start.getTime() - now) / 1000);
  if (left <= 0) return null;
  const pad = (n: number): string => String(n).padStart(2, "0");
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}
